/**
 * ResourcesManagerView — Plano 2 (setup) peça 1: gestão do catálogo de RECURSOS da agenda.
 *
 * O recurso é a unidade-calendário (decisão D1 do Bloco I): o operador cadastra as cadeiras
 * da barbearia, a sala de pilates (capacity>1 = grupo), os profissionais da clínica. Sem
 * recurso cadastrado a agenda nativa não tem o que computar — esta é a tela que a destrava.
 *
 * Gated `scheduling-native` (na página). CRUD sobre /v1/scheduling/resources: criar/editar
 * (If-Match) e aposentar (soft-delete — preserva o histórico). NÃO expõe o vínculo a um User
 * (profissional do sistema) nesta peça — fica para um follow-up com a lista de usuários.
 */
import { Armchair, Box, DoorOpen, Pencil, Plus, User, Users, Wrench, X } from 'lucide-react';
import { useState } from 'react';

import { useResourceActions } from '../../hooks/use-scheduling';
import { useUsers } from '../../hooks/use-users';
import { ApiError, type SchedulingResourceWire } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import {
  COMMON_TIMEZONES,
  DEFAULT_TIMEZONE,
  RESOURCE_TYPE_ORDER,
  resourceTypeLabel,
  resourceTypeLabelPlural,
  type ResourceType,
} from './resource-labels';

import type { JSX } from 'react';

export interface ResourcesManagerViewProps {
  resources: SchedulingResourceWire[];
  canManage: boolean;
}

type FormState =
  | { mode: 'create' }
  | { mode: 'edit'; resource: SchedulingResourceWire }
  | null;

function toast(variant: 'success' | 'error' | 'info', title: string, description?: string): void {
  useToastStore.getState().push({
    variant,
    title,
    ...(description !== undefined && { description }),
    durationMs: variant === 'error' ? 6000 : 3500,
  });
}

export function ResourcesManagerView({ resources, canManage }: ResourcesManagerViewProps): JSX.Element {
  const [form, setForm] = useState<FormState>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const actions = useResourceActions();
  // A1 — nome do profissional (User) vinculado, p/ exibir na linha do recurso.
  const usersQ = useUsers();
  const userNameById = new Map((usersQ.data?.data ?? []).map((u) => [u.id, u.name]));
  const professionalLabel = (resource: SchedulingResourceWire): string | undefined =>
    resource.userId === null ? undefined : (userNameById.get(resource.userId) ?? 'Profissional vinculado');

  const handleDeactivate = (resource: SchedulingResourceWire): void => {
    actions.deactivate.mutate(
      { id: resource.id, version: resource.version },
      {
        onSuccess: () => {
          setConfirmingId(null);
          toast('success', `"${resource.name}" aposentado`, 'Some do catálogo; o histórico é preservado.');
        },
        onError: (err) => {
          setConfirmingId(null);
          if (err instanceof ApiError && err.status === 409) {
            toast('info', 'A lista foi atualizada por outra alteração', 'Recarregamos os recursos. Tente novamente.');
            return;
          }
          toast('error', 'Não foi possível aposentar o recurso.');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div className="flex items-center">
        <p className="text-sm" style={{ color: 'var(--fg-3)' }}>
          {resources.length === 0
            ? 'Nenhum recurso cadastrado ainda.'
            : `${resources.length.toString()} ${resources.length === 1 ? 'recurso' : 'recursos'} no catálogo`}
        </p>
        {canManage && (
          <button type="button" className="btn btn--primary ml-auto" onClick={() => setForm({ mode: 'create' })}>
            <Plus size={14} aria-hidden /> Novo recurso
          </button>
        )}
      </div>

      <div
          className="rounded-md px-4 py-3 text-sm"
          role="note"
          style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', color: 'var(--fg-2)' }}
        >
          Recursos são as unidades que ocupam a agenda: cadeiras, salas, profissionais ou equipamentos.
          Uma sala com <strong>capacidade maior que 1</strong> atende em grupo (ex.: pilates); capacidade 1
          é exclusiva. Defina o horário de funcionamento e os períodos fechados de cada recurso na própria agenda.
        </div>

        {resources.length === 0 && (
          <div
            className="rounded-md px-4 py-8 text-center text-sm"
            style={{ border: '1px dashed var(--border-default)', color: 'var(--fg-3)' }}
          >
            Cadastre o primeiro recurso para começar a montar a agenda.
          </div>
        )}

        {RESOURCE_TYPE_ORDER.map((type) => {
          const rows = resources.filter((r) => r.resourceType === type);
          if (rows.length === 0) return null;
          return (
            <section key={type} aria-label={resourceTypeLabelPlural(type)}>
              <div className="mb-2 flex items-center gap-2">
                <h2
                  className="text-xs font-semibold uppercase"
                  style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}
                >
                  {resourceTypeLabelPlural(type)}
                </h2>
                <span
                  className="text-xs"
                  style={{
                    color: 'var(--fg-3)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-pill)',
                    padding: '0 8px',
                    lineHeight: '18px',
                  }}
                >
                  {rows.length}
                </span>
              </div>
              <ul className="flex flex-col gap-2">
                {rows.map((resource) => {
                  const profName = professionalLabel(resource);
                  return (
                    <ResourceRow
                      key={resource.id}
                      resource={resource}
                      {...(profName !== undefined && { professionalName: profName })}
                      canManage={canManage}
                      isConfirming={confirmingId === resource.id}
                      isBusy={actions.deactivate.isPending}
                      onEdit={() => setForm({ mode: 'edit', resource })}
                      onAskDeactivate={() => setConfirmingId(resource.id)}
                      onCancelDeactivate={() => setConfirmingId(null)}
                      onConfirmDeactivate={() => handleDeactivate(resource)}
                    />
                  );
                })}
              </ul>
            </section>
          );
        })}

      {form !== null && (
        <ResourceFormModal
          mode={form.mode}
          {...(form.mode === 'edit' && { resource: form.resource })}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

/** Ícone Lucide por tipo de recurso (stroke 1.5 — padrão DS). */
function ResourceTypeIcon({ type }: { type: ResourceType }): JSX.Element {
  const size = 15;
  switch (type) {
    case 'professional':
      return <User size={size} aria-hidden />;
    case 'room':
      return <DoorOpen size={size} aria-hidden />;
    case 'chair':
      return <Armchair size={size} aria-hidden />;
    case 'equipment':
      return <Wrench size={size} aria-hidden />;
    default:
      return <Box size={size} aria-hidden />;
  }
}

interface ResourceRowProps {
  resource: SchedulingResourceWire;
  /** A1 — nome do profissional (User) vinculado; ausente = sem vínculo. */
  professionalName?: string;
  canManage: boolean;
  isConfirming: boolean;
  isBusy: boolean;
  onEdit: () => void;
  onAskDeactivate: () => void;
  onCancelDeactivate: () => void;
  onConfirmDeactivate: () => void;
}

function ResourceRow({
  resource,
  professionalName,
  canManage,
  isConfirming,
  isBusy,
  onEdit,
  onAskDeactivate,
  onCancelDeactivate,
  onConfirmDeactivate,
}: ResourceRowProps): JSX.Element {
  const isGroup = resource.capacity > 1;
  return (
    <li
      className="flex items-center gap-3 rounded-md px-4 py-3"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}
    >
      <span
        aria-hidden
        className="flex items-center justify-center rounded-md"
        style={{ width: 28, height: 28, flex: 'none', background: 'var(--bg-surface-2)', color: 'var(--fg-3)' }}
      >
        <ResourceTypeIcon type={resource.resourceType} />
      </span>
      <div className="min-w-0">
        <span className="block truncate font-medium" style={{ color: 'var(--fg-1)' }} title={resource.id}>
          {resource.name}
        </span>
        <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
          {isGroup ? 'Grupo' : 'Exclusivo'}
          {resource.timezone !== DEFAULT_TIMEZONE ? ` · ${resource.timezone}` : ''}
          {professionalName !== undefined && (
            <>
              {' · '}
              <span className="inline-flex items-center gap-1" style={{ color: 'var(--fg-2)' }}>
                <User size={11} aria-hidden /> {professionalName}
              </span>
            </>
          )}
        </span>
      </div>
      {isGroup && (
        <span
          className="ml-auto inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
          style={{ background: 'var(--brand-subtle)', color: 'var(--brand-subtle-fg)' }}
        >
          {resource.capacity} vagas
        </span>
      )}
      {canManage && (
        <div className={`flex items-center gap-2${isGroup ? '' : ' ml-auto'}`}>
          {isConfirming ? (
            <>
              <span className="text-xs" style={{ color: 'var(--fg-3)' }}>
                Aposentar?
              </span>
              <button
                type="button"
                className="btn btn--destructive btn--sm"
                disabled={isBusy}
                onClick={onConfirmDeactivate}
              >
                Confirmar
              </button>
              <button type="button" className="btn btn--ghost btn--sm" disabled={isBusy} onClick={onCancelDeactivate}>
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label={`Editar ${resource.name}`}
                onClick={onEdit}
              >
                <Pencil size={14} aria-hidden /> Editar
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label={`Aposentar ${resource.name}`}
                onClick={onAskDeactivate}
              >
                Aposentar
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

interface ResourceFormModalProps {
  mode: 'create' | 'edit';
  resource?: SchedulingResourceWire;
  onClose: () => void;
}

const FORM_TYPES: readonly ResourceType[] = RESOURCE_TYPE_ORDER;

function ResourceFormModal({ mode, resource, onClose }: ResourceFormModalProps): JSX.Element {
  const actions = useResourceActions();
  const usersQ = useUsers();
  const [name, setName] = useState(resource?.name ?? '');
  const [resourceType, setResourceType] = useState<ResourceType>(resource?.resourceType ?? 'professional');
  const [capacity, setCapacity] = useState(String(resource?.capacity ?? 1));
  const [timezone, setTimezone] = useState(resource?.timezone ?? DEFAULT_TIMEZONE);
  // A1 — vínculo a um profissional (User). '' = sem vínculo. Só usuários ativos no picker.
  const originalUserId = resource?.userId ?? '';
  const [userId, setUserId] = useState<string>(originalUserId);
  const users = (usersQ.data?.data ?? []).filter((u) => u.status === 'active');
  // Preserva um vínculo a usuário fora da página curada (inativo/arquivado) para não perdê-lo.
  const linkedMissing = originalUserId !== '' && !users.some((u) => u.id === originalUserId);

  const capacityNum = Number.parseInt(capacity, 10);
  const valid = name.trim().length > 0 && Number.isInteger(capacityNum) && capacityNum >= 1;
  const busy = actions.create.isPending || actions.update.isPending;

  const handle409 = (err: unknown): boolean => {
    if (err instanceof ApiError && err.status === 409) {
      toast('info', 'A lista foi atualizada por outra alteração', 'Recarregamos os recursos. Reabra e tente novamente.');
      onClose();
      return true;
    }
    return false;
  };

  const onSubmit = (): void => {
    if (!valid || busy) return;
    const body = { name: name.trim(), resourceType, capacity: capacityNum, timezone };
    if (mode === 'create') {
      // Vínculo só entra no POST quando um profissional é escolhido (omitir = sem vínculo).
      const createBody = { ...body, ...(userId !== '' && { userId }) };
      actions.create.mutate(createBody, {
        onSuccess: () => {
          toast('success', `"${body.name}" cadastrado`);
          onClose();
        },
        onError: (err) => {
          if (!handle409(err)) toast('error', 'Não foi possível cadastrar o recurso.');
        },
      });
      return;
    }
    if (resource === undefined) return;
    // Patch parcial: só envia userId se o vínculo mudou (null = desvincular).
    const userIdChanged = userId !== originalUserId;
    actions.update.mutate(
      { id: resource.id, version: resource.version, ...body, ...(userIdChanged && { userId: userId === '' ? null : userId }) },
      {
        onSuccess: () => {
          toast('success', `"${body.name}" atualizado`);
          onClose();
        },
        onError: (err) => {
          if (!handle409(err)) toast('error', 'Não foi possível atualizar o recurso.');
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-label={mode === 'create' ? 'Novo recurso' : `Editar ${resource?.name ?? 'recurso'}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg p-6 shadow-xl" style={{ background: 'var(--bg-surface)' }}>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Novo recurso' : 'Editar recurso'}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            <X size={14} aria-hidden /> Fechar
          </button>
        </div>
        <p className="mb-4 text-xs" style={{ color: 'var(--fg-3)' }}>
          O recurso é a unidade que ocupa a agenda. Capacidade maior que 1 cria um atendimento em grupo.
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Nome</span>
          <input
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="Ex.: Cadeira 1, Sala Pilates, Dra. Ana"
            aria-label="Nome do recurso"
          />
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Tipo</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value as ResourceType)}
              aria-label="Tipo do recurso"
            >
              {FORM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {resourceTypeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block flex items-center gap-1 text-sm font-medium">
              <Users size={13} aria-hidden /> Capacidade
            </span>
            <input
              type="number"
              min={1}
              className="w-full rounded border px-2 py-1"
              style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              aria-label="Capacidade do recurso"
            />
          </label>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium">Fuso horário</span>
          <select
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            aria-label="Fuso horário do recurso"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
            {/* Preserva um fuso fora da lista curada (ex.: recurso legado/seed). */}
            {!COMMON_TIMEZONES.some((tz) => tz.value === timezone) && (
              <option value={timezone}>{timezone}</option>
            )}
          </select>
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block flex items-center gap-1 text-sm font-medium">
            <User size={13} aria-hidden /> Profissional do sistema
            <span className="font-normal" style={{ color: 'var(--fg-3)' }}>
              (opcional)
            </span>
          </span>
          <select
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            aria-label="Profissional do sistema"
          >
            <option value="">Sem profissional vinculado</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
            {/* Preserva o vínculo atual mesmo que o usuário não esteja na página curada. */}
            {linkedMissing && <option value={originalUserId}>Profissional vinculado (fora da lista)</option>}
          </select>
          <span className="mt-1 block text-xs" style={{ color: 'var(--fg-3)' }}>
            Liga este recurso à agenda de um usuário do sistema (ex.: a cadeira do barbeiro João).
          </span>
        </label>

        <button type="button" className="btn btn--primary w-full" onClick={onSubmit} disabled={!valid || busy}>
          {busy ? 'Salvando…' : mode === 'create' ? 'Cadastrar recurso' : 'Salvar alterações'}
        </button>
      </div>
    </div>
  );
}
